import React from 'react';
import { useForm } from 'react-hook-form';
import { Form, Button, Divider } from 'antd';
import { Link } from 'react-router-dom';
import { AxiosError } from 'axios';
import AuthLayout from './auth-layout';
import {
  FormInput,
  FormPassword,
  LoadingState,
  Space,
  useLoading,
} from '@monorepo/react-components';
import {
  AuthErrorCode,
  formEmailValidation,
  formPasswordValidation,
} from '@monorepo/types';
import { useStore } from '../../helpers/use-store';
import { useNotification } from '../../hooks/use-notification';
import GoogleLoginButton from './social/google-login-button';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

interface LoginFormData {
  email: string;
  password: string;
}

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    dataStore: { userStore },
  } = useStore();
  const form = useForm<LoginFormData>();
  const notificationApi = useNotification();
  const { loadingState, updateLoadingState } = useLoading(LoadingState.Initial);

  const getLoginErrorDescription = (e: unknown): string => {
    const data = (e as AxiosError<{ message: string }>)?.response?.data;
    if (data?.message === AuthErrorCode.OAuthOnlyAccount) {
      return t('auth.login.errors.oauthOnly');
    }
    return t('auth.login.errors.invalidCredentials');
  };

  const onSubmit = async (data: LoginFormData) => {
    try {
      updateLoadingState(LoadingState.Loading);
      await userStore.login(data.email, data.password);
    } catch (e) {
      notificationApi.error({
        placement: 'top',
        icon: '',
        message: t('common.error'),
        description: getLoginErrorDescription(e),
      });
    } finally {
      updateLoadingState(LoadingState.Loaded);
    }
  };

  return (
    <AuthLayout title={t('auth.login.title')}>
      <Helmet>
        <title>Login | ShortsNinja</title>
        <meta
          name="description"
          content="Sign in to your ShortsNinja account to access your AI video automation dashboard. Create faceless videos with AI voiceover for your content strategy."
        />
        <meta
          name="keywords"
          content="login, sign in, ShortsNinja account, shorts management, faceless videos, ai video automation, ai voiceover, automated content creation"
        />
      </Helmet>
      <Form onFinish={form.handleSubmit(onSubmit)} layout="vertical">
        <Space size={'large'} direction="vertical">
          <FormInput
            placeholder={t('auth.login.emailPlaceholder')}
            form={form}
            controllerProps={{
              name: 'email',
              rules: formEmailValidation(),
            }}
          />
          <FormPassword
            placeholder={t('auth.login.passwordPlaceholder')}
            form={form}
            controllerProps={{
              name: 'password',
              rules: formPasswordValidation(),
            }}
          />
          <Button
            loading={loadingState === LoadingState.Loading}
            type="primary"
            htmlType="submit"
            block
          >
            {t('auth.login.loginButton')}
          </Button>
        </Space>
      </Form>
      <Divider plain>{t('common.or')}</Divider>
      <GoogleLoginButton />

      <div className="auth-link">
        {t('auth.login.noAccount')}{' '}
        <Link to="/register">{t('auth.login.signUp')}</Link>
      </div>
      <div className="auth-link">
        <Link to="/forget-password">{t('auth.login.forgotPassword')}</Link>
      </div>
    </AuthLayout>
  );
};

export default LoginPage;
